import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Row,
  Col,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'All encounters', value: '' },
  { label: 'Open', value: 0 },
  { label: 'Closed', value: 1 },
];

const formatMoney = (value) =>
  `₦${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = dayjs(typeof value === 'string' ? value.replace(' ', 'T') : value);
  return d.isValid() ? d.format('DD MMM YYYY, HH:mm') : '—';
};

/**
 * Encounter roll-up for a facility group: one row per facility (main first,
 * then branches by volume) plus a group total.
 *
 * Backed by GET /v2/checkins/group-summary, which only expands to the whole
 * group when the facility asked about is the group's MAIN facility — a branch
 * gets its own numbers and nothing else. Facilities with no encounters in the
 * window still appear, because "nothing happened at this branch" is an answer.
 */
export default function FacilityGroupEncounters({ facilityId, title = 'Encounters across the group' }) {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [range, setRange] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { facility_id: facilityId };
      if (status !== '') params.status = status;
      if (range?.[0]) params.date_from = range[0].format('YYYY-MM-DD');
      if (range?.[1]) params.date_to = range[1].format('YYYY-MM-DD');

      const parsed = lib.parseApiEnvelope(await lib.getGroupEncounterSummaryV2(params));
      if (!parsed.ok) {
        setError(parsed.message || 'Failed to load encounter summary');
        setSummary(null);
        return;
      }
      setSummary(parsed.data || null);
    } catch (e) {
      setError(e?.message || 'Failed to load encounter summary');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [facilityId, status, range]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => summary?.facilities || [], [summary]);
  const totals = summary?.totals;

  const columns = [
    {
      title: 'Facility',
      dataIndex: 'facility_name',
      key: 'facility_name',
      render: (value, row) => (
        <Space>
          <Text strong>{value || `Facility #${row.facility_id}`}</Text>
          {row.is_main ? <Tag color="gold">Main</Tag> : <Tag color="blue">Branch</Tag>}
        </Space>
      ),
    },
    { title: 'Encounters', dataIndex: 'encounters', key: 'encounters', align: 'right' },
    { title: 'Open', dataIndex: 'open_encounters', key: 'open_encounters', align: 'right' },
    { title: 'Closed', dataIndex: 'closed_encounters', key: 'closed_encounters', align: 'right' },
    { title: 'Enrollees', dataIndex: 'unique_enrollees', key: 'unique_enrollees', align: 'right' },
    {
      title: 'Requested',
      dataIndex: 'requested_total',
      key: 'requested_total',
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Approved',
      dataIndex: 'approved_total',
      key: 'approved_total',
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Last encounter',
      dataIndex: 'last_encounter_at',
      key: 'last_encounter_at',
      render: formatDateTime,
    },
  ];

  if (loading && !summary) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
        <Space wrap>
          <Select
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
            style={{ minWidth: 160 }}
          />
          <RangePicker value={range} onChange={setRange} allowEmpty={[true, true]} />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        </Space>
      </Space>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

      {!summary?.has_branches ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="This facility has no branches, so these are its own encounters."
        />
      ) : null}

      {totals ? (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}><Statistic title="Encounters" value={totals.encounters} /></Col>
          <Col xs={12} md={6}><Statistic title="Open" value={totals.open_encounters} /></Col>
          <Col xs={12} md={6}><Statistic title="Enrollees" value={totals.unique_enrollees} /></Col>
          <Col xs={12} md={6}>
            <Statistic title="Approved" value={totals.approved_total} formatter={formatMoney} />
          </Col>
        </Row>
      ) : null}

      {rows.length === 0 ? (
        <Empty description="No encounters in this window" />
      ) : (
        <Table
          rowKey="facility_id"
          size="small"
          dataSource={rows}
          columns={columns}
          pagination={false}
          loading={loading}
          summary={() =>
            totals ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><Text strong>Group total</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right"><Text strong>{totals.encounters}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><Text strong>{totals.open_encounters}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><Text strong>{totals.closed_encounters}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><Text strong>{totals.unique_enrollees}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right"><Text strong>{formatMoney(totals.requested_total)}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right"><Text strong>{formatMoney(totals.approved_total)}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={7} />
              </Table.Summary.Row>
            ) : null
          }
        />
      )}
    </div>
  );
}
